export function polyfillURLSearchParams(): void {
  if (typeof URLSearchParams !== 'undefined') {
    return;
  }

  class URLSearchParamsPolyfill {
    private params: Map<string, string[]>;

    constructor(init?: string | URLSearchParamsPolyfill) {
      this.params = new Map();

      if (typeof init === 'string') {
        this.parseQueryString(init);
      } else if (init instanceof URLSearchParamsPolyfill) {
        init.forEach((value, key) => {
          this.append(key, value);
        });
      }
    }

    private parseQueryString(query: string): void {
      const cleanQuery = query.startsWith('?') ? query.slice(1) : query;
      if (!cleanQuery) return;

      cleanQuery.split('&').forEach((param) => {
        const [key, value] = param.split('=');
        if (key) {
          const decodedKey = this.safeDecodeURIComponent(key);
          const decodedValue = value ? this.safeDecodeURIComponent(value) : '';
          this.append(decodedKey, decodedValue);
        }
      });
    }

    private safeDecodeURIComponent(str: string): string {
      try {
        return decodeURIComponent(str.replace(/\+/g, ' '));
      } catch (e) {
        return str;
      }
    }

    append(name: string, value: string): void {
      const values = this.params.get(name) || [];
      values.push(String(value));
      this.params.set(name, values);
    }

    delete(name: string): void {
      this.params.delete(name);
    }

    get(name: string): string | null {
      const values = this.params.get(name);
      return values && values.length > 0 && values[0] !== undefined ? values[0] : null;
    }

    getAll(name: string): string[] {
      return this.params.get(name) || [];
    }

    has(name: string): boolean {
      return this.params.has(name);
    }

    set(name: string, value: string): void {
      this.params.set(name, [String(value)]);
    }

    forEach(callback: (value: string, key: string, parent: URLSearchParamsPolyfill) => void): void {
      this.params.forEach((values, key) => {
        values.forEach((value) => {
          callback(value, key, this);
        });
      });
    }

    toString(): string {
      const parts: string[] = [];
      this.params.forEach((values, key) => {
        values.forEach((value) => {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        });
      });
      return parts.join('&');
    }
  }

  // @ts-ignore
  window.URLSearchParams = URLSearchParamsPolyfill;
}

export function polyfillTextEncoder(): void {
  if (typeof TextEncoder !== 'undefined') {
    return;
  }

  class TextEncoderPolyfill {
    encoding = 'utf-8';

    encode(str: string): Uint8Array {
      const utf8: number[] = [];
      for (let i = 0; i < str.length; i++) {
        let charcode = str.charCodeAt(i);
        if (charcode < 0x80) {
          utf8.push(charcode);
        } else if (charcode < 0x800) {
          utf8.push(0xc0 | (charcode >> 6), 0x80 | (charcode & 0x3f));
        } else if (charcode < 0xd800 || charcode >= 0xe000) {
          utf8.push(
            0xe0 | (charcode >> 12),
            0x80 | ((charcode >> 6) & 0x3f),
            0x80 | (charcode & 0x3f)
          );
        } else {
          i++;
          charcode = 0x10000 + (((charcode & 0x3ff) << 10) | (str.charCodeAt(i) & 0x3ff));
          utf8.push(
            0xf0 | (charcode >> 18),
            0x80 | ((charcode >> 12) & 0x3f),
            0x80 | ((charcode >> 6) & 0x3f),
            0x80 | (charcode & 0x3f)
          );
        }
      }
      return new Uint8Array(utf8);
    }
  }

  // @ts-ignore
  window.TextEncoder = TextEncoderPolyfill;
}

export function polyfillPromiseFinally(): void {
  if (typeof Promise !== 'undefined' && !Promise.prototype.finally) {
    Promise.prototype.finally = function (callback: () => void) {
      const constructor = this.constructor as PromiseConstructor;
      return this.then(
        (value) => constructor.resolve(callback()).then(() => value),
        (reason) =>
          constructor.resolve(callback()).then(() => {
            throw reason;
          })
      );
    };
  }
}

export function polyfillObjectAssign(): void {
  if (typeof Object.assign !== 'function') {
    Object.assign = function (target: any, ...sources: any[]) {
      if (target == null) {
        throw new TypeError('Cannot convert undefined or null to object');
      }

      const to = Object(target);

      for (let i = 0; i < sources.length; i++) {
        const nextSource = sources[i];

        if (nextSource != null) {
          for (const nextKey in nextSource) {
            if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
              to[nextKey] = nextSource[nextKey];
            }
          }
        }
      }

      return to;
    };
  }
}

export function polyfillArrayFrom(): void {
  if (!Array.from) {
    Array.from = function (arrayLike: any, mapFn?: any, thisArg?: any) {
      const items = Object(arrayLike);
      if (arrayLike == null) {
        throw new TypeError('Array.from requires an array-like object');
      }

      const len = items.length >>> 0;
      const result = new Array(len);

      for (let i = 0; i < len; i++) {
        if (mapFn) {
          result[i] = mapFn.call(thisArg, items[i], i);
        } else {
          result[i] = items[i];
        }
      }

      return result;
    };
  }
}

export function polyfillStringStartsWith(): void {
  if (!String.prototype.startsWith) {
    String.prototype.startsWith = function (search: string, pos?: number) {
      pos = !pos || pos < 0 ? 0 : +pos;
      return this.substring(pos, pos + search.length) === search;
    };
  }
}

export function polyfillStringEndsWith(): void {
  if (!String.prototype.endsWith) {
    String.prototype.endsWith = function (search: string, length?: number) {
      if (length === undefined || length > this.length) {
        length = this.length;
      }
      return this.substring(length - search.length, length) === search;
    };
  }
}

export function polyfillStringIncludes(): void {
  if (!String.prototype.includes) {
    String.prototype.includes = function (search: string, start?: number) {
      if (typeof start !== 'number') {
        start = 0;
      }
      if (start + search.length > this.length) {
        return false;
      }
      return this.indexOf(search, start) !== -1;
    };
  }
}

export function initializePolyfills(): void {
  polyfillObjectAssign();
  polyfillArrayFrom();
  polyfillStringStartsWith();
  polyfillStringEndsWith();
  polyfillStringIncludes();
  polyfillURLSearchParams();
  polyfillTextEncoder();
  polyfillPromiseFinally();
}

